import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { supabase } from '../lib/supabase';
import { defaultTransition } from '../utils/animations';

export default function Register() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const isSubmittingRef = useRef(false);
  const [formData, setFormData] = useState({
    prenume: '',
    nume: '',
    cnp: '',
    telefon: '',
    localitate: '',
    judet: '',
    email: '',
    password: '',
  });

  const formatText = (text: string): string => {
    return text
      .trim()
      .toLowerCase()
      .split(' ')
      .filter((word) => word.length > 0)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Prevent multiple submissions
    if (isSubmittingRef.current) {
      return;
    }

    isSubmittingRef.current = true;
    setLoading(true);
    setError(null);

    const submissionId =
      Date.now().toString() + Math.random().toString(36).substr(2, 9);

    // Format the text fields before validation and submission
    const formattedData = {
      ...formData,
      nume: formatText(formData.nume),
      prenume: formatText(formData.prenume),
      localitate: formatText(formData.localitate),
      judet: formatText(formData.judet),
    };

    // Validate phone number format if provided
    if (!formattedData.telefon) {
      setError('Numărul de telefon este obligatoriu');
      setLoading(false);
      isSubmittingRef.current = false; // Reset submission lock
      return;
    }

    if (!/^\+?[0-9]{1,25}$/.test(formattedData.telefon)) {
      setError(
        'Numărul de telefon poate conține doar cifre și simbolul + la început'
      );
      setLoading(false);
      isSubmittingRef.current = false; // Reset submission lock
      return;
    }

    if (!/^\d{13}$/.test(formattedData.cnp)) {
      setError('CNP-ul trebuie să conțină exact 13 cifre');
      setLoading(false);
      isSubmittingRef.current = false; // Reset submission lock
      return;
    }

    try {
      const { data, error: signUpError } = await supabase.auth.signUp({
        email: formattedData.email,
        password: formattedData.password,
        options: {
          data: {
            prenume: formattedData.prenume,
            nume: formattedData.nume,
            cnp: formattedData.cnp,
            telefon: formattedData.telefon || null,
            localitate: formattedData.localitate,
            judet: formattedData.judet,
            role: 'pacient',
          },
        },
      });

      if (signUpError) throw signUpError;

      if (data.user) {
        // Send webhook to Make.com with registration data and wait for 200 response
        try {
          const webhookResponse = await fetch(
            'https://n8n.ovi.net.eu.org/webhook/cont-nou',
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                nume: formattedData.nume,
                prenume: formattedData.prenume,
                cnp: formattedData.cnp,
                telefon: formattedData.telefon,
                localitate: formattedData.localitate,
                judet: formattedData.judet,
                email: formattedData.email,
                submission_id: submissionId,
              }),
            }
          );

          if (!webhookResponse.ok) {
            console.error(
              'Webhook failed with status:',
              webhookResponse.status,
              'Response:',
              await webhookResponse.text()
            );
            // Log the error but don't disrupt user flow
          }
        } catch (webhookError) {
          console.error('Webhook error:', webhookError);
          // Silent fail - don't disrupt user flow
        }

        setShowSuccessMessage(true);
        setTimeout(() => {
          navigate('/');
        }, 5000);
      }
    } catch (err) {
      console.error('Signup error:', err);
      setError(
        err instanceof Error ? err.message : 'A apărut o eroare la înregistrare'
      );
    } finally {
      setLoading(false);
      isSubmittingRef.current = false;
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (['nume', 'prenume', 'localitate', 'judet'].includes(name)) {
      const formattedValue = formatText(value);
      setFormData((prev) => ({
        ...prev,
        [name]: formattedValue,
      }));
    }
  };

  return (
    <div className="relative min-h-screen bg-gradient-to-br from-[#024994] via-[#012a52] to-[#0A0E27] flex flex-col justify-center py-12 sm:px-6 lg:px-8 overflow-hidden">
      {/* Enhanced Animated Background - Same as Login */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          className="absolute top-20 left-10 w-96 h-96 bg-cyan-500/15 rounded-full blur-3xl"
          animate={{
            y: [-15, 15, -15],
            x: [-10, 10, -10],
            transition: {
              duration: 10,
              repeat: Infinity,
              ease: 'easeInOut',
            },
          }}
        />
        <motion.div
          className="absolute bottom-20 right-10 w-80 h-80 bg-blue-500/15 rounded-full blur-3xl"
          animate={{
            y: [15, -15, 15],
            transition: {
              duration: 12,
              repeat: Infinity,
              ease: 'easeInOut',
            },
          }}
        />
        <motion.div
          className="absolute top-1/3 right-1/4 w-64 h-64 bg-purple-500/10 rounded-full blur-3xl"
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.4, 0.6, 0.4],
            transition: {
              duration: 8,
              repeat: Infinity,
              ease: 'easeInOut',
            },
          }}
        />
        <motion.div
          className="absolute bottom-1/3 left-1/4 w-72 h-72 bg-cyan-400/10 rounded-full blur-3xl"
          animate={{
            y: [-10, 10, -10],
            rotate: [0, 180, 0],
            transition: {
              duration: 15,
              repeat: Infinity,
              ease: 'easeInOut',
            },
          }}
        />

        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.05)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.05)_1px,transparent_1px)] bg-[size:50px_50px]" />

        {[...Array(30)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-cyan-400/40 rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, -100, 0],
              opacity: [0, 0.8, 0],
              transition: {
                duration: 4 + Math.random() * 3,
                repeat: Infinity,
                delay: Math.random() * 3,
                ease: 'easeInOut',
              },
            }}
          />
        ))}
      </div>

      <motion.div
        className="relative sm:mx-auto sm:w-full sm:max-w-md"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={defaultTransition}
      >
        <motion.h2
          className="text-center text-3xl font-extrabold text-white drop-shadow-lg"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ ...defaultTransition, delay: 0.1 }}
        >
          Înregistrare cont nou
        </motion.h2>
      </motion.div>

      {/* Important notification box */}
      <motion.div
        className="relative mt-4 sm:mx-auto sm:w-full sm:max-w-md"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ ...defaultTransition, delay: 0.15 }}
      >
        <div className="bg-amber-500/20 border-l-4 border-amber-400 p-4 rounded-r-lg backdrop-blur-sm">
          <p className="text-amber-100 font-medium">
            În cazul în care sunteți aparținătorul pacientului, vă rugăm să
            completați câmpurile Prenume, Nume și CNP cu datele acestuia. Adresa
            de e-mail și numărul de telefon pot să fie ale aparținătorului
          </p>
        </div>
      </motion.div>

      <motion.div
        className="relative mt-8 sm:mx-auto sm:w-full sm:max-w-md"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ ...defaultTransition, delay: 0.2 }}
      >
        <div
          className="bg-white/10 backdrop-blur-xl py-8 px-4 shadow-2xl sm:rounded-2xl sm:px-10 border border-white/20"
        >
          {showSuccessMessage ? (
            <motion.div
              className="bg-green-500/20 border-l-4 border-green-400 p-4 backdrop-blur-sm rounded"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
            >
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg
                    className="h-5 w-5 text-green-400"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-green-100">
                    Te rugăm să activezi contul folosind link-ul primit pe
                    e-mail. Dacă nu găsești mail-ul în Inbox, te rugăm să cauți
                    în dosarul Spam
                  </p>
                </div>
              </div>
            </motion.div>
          ) : (
            <form
              className="space-y-6"
              onSubmit={handleSubmit}
              id="register-form"
              name="register-form"
            >
              <div>
                <label
                  htmlFor="register-nume"
                  className="block text-sm font-medium text-white"
                >
                  Nume
                </label>
                <div className="mt-1">
                  <input
                    id="register-nume"
                    name="nume"
                    type="text"
                    required
                    value={formData.nume}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className="appearance-none block w-full px-3 py-2 bg-white/90 border border-white/30 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-transparent focus:bg-white transition"
                  />
                </div>
              </div>

              <div>
                <label
                  htmlFor="register-prenume"
                  className="block text-sm font-medium text-white"
                >
                  Prenume
                </label>
                <div className="mt-1">
                  <input
                    id="register-prenume"
                    name="prenume"
                    type="text"
                    required
                    value={formData.prenume}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className="appearance-none block w-full px-3 py-2 bg-white/90 border border-white/30 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-transparent focus:bg-white transition"
                  />
                </div>
              </div>

              <div>
                <label
                  htmlFor="register-cnp"
                  className="block text-sm font-medium text-white"
                >
                  CNP
                </label>
                <div className="mt-1">
                  <input
                    id="register-cnp"
                    name="cnp"
                    type="text"
                    required
                    pattern="[0-9]{13}"
                    maxLength={13}
                    value={formData.cnp}
                    onChange={handleChange}
                    className="appearance-none block w-full px-3 py-2 bg-white/90 border border-white/30 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-transparent focus:bg-white transition"
                  />
                </div>
              </div>

              <div>
                <label
                  htmlFor="register-telefon"
                  className="block text-sm font-medium text-white"
                >
                  Nr. Telefon
                </label>
                <div className="mt-1">
                  <input
                    id="register-telefon"
                    name="telefon"
                    type="text"
                    required
                    pattern="^\+?[0-9]{1,25}$"
                    value={formData.telefon}
                    onChange={handleChange}
                    className="appearance-none block w-full px-3 py-2 bg-white/90 border border-white/30 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-transparent focus:bg-white transition"
                  />
                </div>
              </div>

              <div>
                <label
                  htmlFor="register-localitate"
                  className="block text-sm font-medium text-white"
                >
                  Localitate
                </label>
                <div className="mt-1">
                  <input
                    id="register-localitate"
                    name="localitate"
                    type="text"
                    required
                    value={formData.localitate}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className="appearance-none block w-full px-3 py-2 bg-white/90 border border-white/30 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-transparent focus:bg-white transition"
                  />
                </div>
              </div>

              <div>
                <label
                  htmlFor="register-judet"
                  className="block text-sm font-medium text-white"
                >
                  Județ
                </label>
                <div className="mt-1">
                  <input
                    id="register-judet"
                    name="judet"
                    type="text"
                    required
                    value={formData.judet}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className="appearance-none block w-full px-3 py-2 bg-white/90 border border-white/30 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-transparent focus:bg-white transition"
                  />
                </div>
              </div>

              <div>
                <label
                  htmlFor="register-email"
                  className="block text-sm font-medium text-white"
                >
                  Adresă de e-mail
                </label>
                <div className="mt-1">
                  <input
                    id="register-email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="appearance-none block w-full px-3 py-2 bg-white/90 border border-white/30 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-transparent focus:bg-white transition"
                  />
                </div>
              </div>

              <div>
                <label
                  htmlFor="register-password"
                  className="block text-sm font-medium text-white"
                >
                  Parolă
                </label>
                <div className="mt-1">
                  <input
                    id="register-password"
                    name="password"
                    type="password"
                    required
                    value={formData.password}
                    onChange={handleChange}
                    className="appearance-none block w-full px-3 py-2 bg-white/90 border border-white/30 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-transparent focus:bg-white transition"
                  />
                </div>
              </div>

              {error && (
                <motion.div
                  className="text-red-300 text-sm bg-red-500/20 p-3 rounded-md border border-red-400/30"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  {error}
                </motion.div>
              )}

              <div>
                <motion.button
                  type="submit"
                  disabled={loading}
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-lg text-sm font-medium text-white bg-gradient-to-r from-[#024994] to-[#012a52] hover:from-[#012a52] hover:to-[#024994] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-400 disabled:opacity-50 transition"
                  whileHover={{
                    scale: 1.02,
                    boxShadow: '0 0 20px rgba(0, 217, 255, 0.5)',
                  }}
                  whileTap={{ scale: 0.98 }}
                >
                  {loading ? 'Se procesează...' : 'Înregistrează-mă'}
                </motion.button>
              </div>
            </form>
          )}
        </div>
      </motion.div>
    </div>
  );
}
